import * as THREE from 'three';
import { RESTROOMS } from '../config/restrooms';
import type { LevelDefinition } from '../world/Level';

/** Floor stains and loose paper have no counterpart in the collision world. */
export function buildRestroomMess(level: LevelDefinition): THREE.Group {
  const root = new THREE.Group();
  root.name = 'wc-lattian-sotkut';
  const floor = level.floors.find((item) => item.name === 'wc-lattia');
  if (!floor) return root;

  const wet = new THREE.MeshPhongMaterial({
    ...RESTROOMS.urine,
    specular: 0xeee1a0,
    transparent: true,
    depthWrite: false,
  });
  const paper = new THREE.MeshLambertMaterial({ color: RESTROOMS.paper.color, side: THREE.DoubleSide });
  const core = new THREE.MeshLambertMaterial({ color: RESTROOMS.paper.coreColor, side: THREE.DoubleSide });
  const toilets = level.decor.filter((item) => item.name.startsWith('wc-pontto-'));

  toilets.forEach((toilet, index) => {
    const group = new THREE.Group();
    group.name = `${toilet.name}-sotkut`;
    group.position.set(toilet.box.maxX, floor.y + RESTROOMS.floorOffset,
      (toilet.box.minZ + toilet.box.maxZ) / 2);
    root.add(group);

    RESTROOMS.puddles.forEach((puddle, at) => {
      const outline = new THREE.Shape();
      for (let i = 0; i < 40; i++) {
        const angle = i / 40 * Math.PI * 2;
        const radius = 1 + 0.13 * Math.sin(angle * 3 + index + at)
          + 0.07 * Math.cos(angle * 7 - index);
        const x = Math.cos(angle) * radius * puddle.radiusX;
        const z = Math.sin(angle) * radius * puddle.radiusZ;
        if (i === 0) outline.moveTo(x, z);
        else outline.lineTo(x, z);
      }
      outline.closePath();
      const mesh = new THREE.Mesh(new THREE.ShapeGeometry(outline), wet);
      mesh.name = `virtsalammikko-${at + 1}`;
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(puddle.x, 0, puddle.z);
      group.add(mesh);
    });

    if (RESTROOMS.paper.toilets.some((name) => name === toilet.name)) {
      const spec = RESTROOMS.paper;
      const roll = new THREE.Group();
      roll.name = 'vessapaperirulla';
      roll.position.set(spec.x, spec.radius, spec.z);
      // A hollow roll lying on its side, with the cardboard tube visible.
      const profile = [
        new THREE.Vector2(spec.coreRadius, -spec.width / 2),
        new THREE.Vector2(spec.radius, -spec.width / 2),
        new THREE.Vector2(spec.radius, spec.width / 2),
        new THREE.Vector2(spec.coreRadius, spec.width / 2),
      ];
      const layers = new THREE.Mesh(new THREE.LatheGeometry(profile, 24), paper);
      layers.rotation.x = Math.PI / 2;
      roll.add(layers);
      const tube = new THREE.Mesh(new THREE.CylinderGeometry(
        spec.coreRadius, spec.coreRadius, spec.width, 24, 1, true,
      ), core);
      tube.rotation.x = Math.PI / 2;
      roll.add(tube);
      group.add(roll);

      // A bent ribbon holds small folds above the floor instead of looking painted on.
      const ribbon = new THREE.PlaneGeometry(spec.length, spec.width, 32, 1);
      ribbon.rotateX(-Math.PI / 2);
      const points = ribbon.getAttribute('position');
      for (let i = 0; i < points.count; i++) {
        const along = points.getX(i) / spec.length + 0.5;
        points.setY(i, 0.001 + spec.foldHeight * Math.pow(Math.sin(along * Math.PI * 5), 2));
        points.setZ(i, points.getZ(i) + Math.sin(along * Math.PI * 2) * 0.025);
      }
      ribbon.computeVertexNormals();
      const strip = new THREE.Mesh(ribbon, paper);
      strip.name = 'vessapaperisuikale';
      strip.position.set(spec.x + spec.length / 2, 0, spec.z);
      group.add(strip);
    }
  });
  return root;
}
